import React, { useMemo, useState } from 'react';
import { Workflow } from 'lucide-react';
import ConfirmPopup from '../PurchaseProgram/ConfirmPopup';
import PipelineStepRow from './PipelineStepRow';
import ScheduleDrawer from './ScheduleDrawer';
import { PIPELINE_ACTIVE, PIPELINE_PHASES } from './pipelineConstants';
import usePipelineActions from './usePipelineActions';

function PipelineTimeline({
  steps,
  getAuthHeaders,
  onRefresh,
  pipelineMessage,
  onPipelineMessage,
  displayTick = 0,
}) {
  const [confirm, setConfirm] = useState(null);
  const [scheduleStep, setScheduleStep] = useState(null);
  const stepsById = useMemo(() => {
    const map = {};
    (steps || []).forEach((step) => {
      map[step.id] = step;
    });
    return map;
  }, [steps]);

  const { startingId, startStep } = usePipelineActions({
    getAuthHeaders,
    onSuccess: (step, result) => {
      // pipeline_runner.run_step returns {file_found: false, ...} when the
      // FTP poll succeeded but no file matched the pattern. Show that as an
      // info message so the user knows the scheduler ran but had nothing to do.
      if (result && result.file_found === false) {
        onPipelineMessage?.({
          type: 'info',
          text: `${step.label}: checked but no file matched (${
            result.message || 'no matching file on remote'
          }).`,
        });
      } else {
        const jobId = result?.job_id || result?.import?.job_id;
        onPipelineMessage?.({
          type: 'success',
          text: `${step.label} started${jobId ? ` (job ${jobId})` : ''}.`,
        });
      }
      onRefresh?.();
    },
    onError: (message) => {
      onPipelineMessage?.({ type: 'danger', text: message });
    },
  });

  const requestStart = (step) => {
    if (!step.can_start) {
      onPipelineMessage?.({
        type: 'warning',
        text: step.start_hint || 'Open the manage page to run this step.',
      });
      return;
    }
    if (PIPELINE_ACTIVE.has(String(step.status || '').toLowerCase())) {
      onPipelineMessage?.({ type: 'warning', text: 'This step is already running.' });
      return;
    }

    const modeLabel = step.id === 'product_data_sync' ? 'delta' : '';
    setConfirm({
      step,
      title: `Start ${step.label}?`,
      message: modeLabel
        ? `This will queue a ${modeLabel} ${step.label.toLowerCase()} for your account.`
        : `This will queue ${step.label.toLowerCase()} for your account.`,
    });
  };

  const handleConfirm = async () => {
    const step = confirm?.step;
    setConfirm(null);
    if (step) await startStep(step);
  };

  const requestSchedule = (step) => setScheduleStep(step);

  const handleScheduleSaved = (step) => {
    onPipelineMessage?.({
      type: 'success',
      text: `Schedule saved for ${step.label}.`,
    });
    onRefresh?.();
  };

  const handleScheduleDeleted = (step) => {
    onPipelineMessage?.({
      type: 'success',
      text: `Schedule removed for ${step.label}.`,
    });
    onRefresh?.();
  };

  const handleScheduleError = (message) => {
    onPipelineMessage?.({ type: 'danger', text: message });
  };

  const handleDataSourceSaved = (step, dataSource) => {
    onPipelineMessage?.({
      type: 'success',
      text: `Remote data source saved for ${step.label}.`,
    });
    if (dataSource) {
      setScheduleStep((prev) => (prev?.id === step.id
        ? { ...prev, data_source: dataSource, data_source_enabled: true }
        : prev));
    }
    onRefresh?.();
  };

  const handleDataSourceDeleted = (step) => {
    onPipelineMessage?.({
      type: 'success',
      text: `Remote data source removed for ${step.label}.`,
    });
    setScheduleStep((prev) => (prev?.id === step.id
      ? { ...prev, data_source: null }
      : prev));
    onRefresh?.();
  };

  const hasSteps = PIPELINE_PHASES.some((phase) =>
    phase.stepIds.some((id) => stepsById[id]),
  );
  if (!hasSteps) return null;

  const activeCount = (steps || []).filter((s) =>
    PIPELINE_ACTIVE.has(String(s.status || '').toLowerCase()),
  ).length;

  return (
    <>
      <section className="ahri-dash-section ahri-dash-pipeline mb-4">
        <div className="ahri-dash-section__head ahri-dash-section__head--primary">
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <span className="ahri-dash-section__icon">
              <Workflow size={22} strokeWidth={2} />
            </span>
            <div>
              <h5 className="mb-0 fw-semibold text-dark">AHRI data pipeline</h5>
              <p className="mb-0 small text-muted">
                GraphQL sync → product index → data quality → inventory → product mapping → attribute mapping
              </p>
            </div>
          </div>
          {activeCount > 0 ? (
            <span className="ahri-dash-pipeline__live badge rounded-pill">
              {activeCount} job{activeCount === 1 ? '' : 's'} running
            </span>
          ) : null}
        </div>

        {pipelineMessage?.text ? (
          <div
            className={`alert alert-${pipelineMessage.type || 'info'} border-0 rounded-0 mb-0 py-2 px-3`}
            role="status"
          >
            {pipelineMessage.text}
          </div>
        ) : null}

        <div className="ahri-dash-pipeline__legend row g-0 border-bottom small text-muted">
          <div className="col-md-6 py-2 px-3">Step · source · status</div>
          <div className="col-md-3 py-2 px-3 d-none d-md-block">Last synced &amp; next run</div>
          <div className="col-md-3 py-2 px-3 d-none d-md-block text-md-end">Actions</div>
        </div>

        {PIPELINE_PHASES.map((phase) => {
          const phaseSteps = phase.stepIds
            .map((id) => stepsById[id])
            .filter(Boolean)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          if (!phaseSteps.length) return null;

          return (
            <div key={phase.id} className="ahri-dash-pipeline__phase">
              <div className="ahri-dash-pipeline__phase-head px-3 py-2">
                <h6 className="mb-0 fw-semibold">{phase.title}</h6>
                <p className="mb-0 small text-muted">{phase.subtitle}</p>
              </div>
              <ol className="ahri-dash-pipeline__track list-unstyled mb-0">
                {phaseSteps.map((step, index) => (
                  <PipelineStepRow
                    key={step.id}
                    step={step}
                    isLast={index === phaseSteps.length - 1}
                    startingId={startingId}
                    onRequestStart={requestStart}
                    onRequestSchedule={requestSchedule}
                    displayTick={displayTick}
                  />
                ))}
              </ol>
            </div>
          );
        })}
      </section>

      <ConfirmPopup
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel="Start"
        variant="warning"
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      <ScheduleDrawer
        open={Boolean(scheduleStep)}
        step={scheduleStep}
        getAuthHeaders={getAuthHeaders}
        onClose={() => setScheduleStep(null)}
        onSaved={handleScheduleSaved}
        onDeleted={handleScheduleDeleted}
        onError={handleScheduleError}
        onDataSourceSaved={handleDataSourceSaved}
        onDataSourceDeleted={handleDataSourceDeleted}
      />
    </>
  );
}

export default PipelineTimeline;
